// billing-settings.tsx

import { useEffect, useState } from 'react';
import { useSettingsContext } from '../settings-context';
import { useModalContext } from '../modal-context';

import UpgradeNowSanctumModal from './modals/upgrade-sanctum/upgrade-sanctum';
import UpgradePlanModal from './modals/upgrade-now/upgrade-now';

import { /* ActivePlanPanel, */ PaymentMethodPanel, InvoicesPanel, BillingDetailsPanel } from './panels';

// Import types from the modal hooks (they use 'Monthly' | 'Annual')
import type { UpgradeNowConfirmPayload } from './modals/upgrade-now/upgrade-now.hooks';
import type { UpgradeSanctumPayload } from './modals/upgrade-sanctum/upgrade-sanctum.hooks';

import type { BillingCycle } from '../settings.types';

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

interface BillingSettingsProps {
  onComparePlansRequested?: () => void;
  onCancelPlan?: () => void;
  /** External trigger from Settings to open Sanctum "Upgrade Now" directly */
  externalOpenSanctumUpgrade?: boolean;
  /** Callback to acknowledge external trigger consumption */
  onExternalOpenSanctumUpgradeHandled?: () => void;
  /** Profile email from parent (for billing email fallback) */
  profileEmail?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const formatTrialEndDate = (dateString?: string): string => {
  if (!dateString) return 'Unknown';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

/** Map modal BillingCycle ('Monthly'|'Annual') to checkout BillingCycle ('monthly'|'annual') */
const toCheckoutBillingCycle = (cycle: 'Monthly' | 'Annual'): BillingCycle =>
  cycle === 'Annual' ? 'annual' : 'monthly';

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export const BillingSettings = ({
  onComparePlansRequested: _onComparePlansRequested,
  onCancelPlan: _onCancelPlan,
  externalOpenSanctumUpgrade,
  onExternalOpenSanctumUpgradeHandled,
  profileEmail,
}: BillingSettingsProps) => {
  const { planName, trialEnd } = useSettingsContext();
  const { openModal } = useModalContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // Upgrade Modals
  // ─────────────────────────────────────────────────────────────────────────────

  // Trial "Upgrade now" -> original UpgradePlanModal
  const [upgradeTrialOpen, setUpgradeTrialOpen] = useState(false);
  const closeUpgradeTrial = () => setUpgradeTrialOpen(false);

  const handleUpgradeConfirmTrial = (payload: UpgradeNowConfirmPayload): void => {
    closeUpgradeTrial();
    // Open the checkout modal with the selected plan & billing cycle
    openModal('checkout', {
      billingCycle: toCheckoutBillingCycle(payload.billingCycle),
      planName: payload.plan,
    });
  };

  // Sanctum header "Upgrade" -> new UpgradeNowSanctumModal
  const [upgradeSanctumOpen, setUpgradeSanctumOpen] = useState(false);
  const openUpgradeSanctum = () => setUpgradeSanctumOpen(true);
  const closeUpgradeSanctum = () => setUpgradeSanctumOpen(false);

  const handleUpgradeConfirmSanctum = (payload: UpgradeSanctumPayload): void => {
    closeUpgradeSanctum();
    // Open the checkout modal with the selected plan & billing cycle
    openModal('checkout', {
      billingCycle: toCheckoutBillingCycle(payload.billingCycle),
      planName: payload.plan,
    });
  };

  // Handle external "open Sanctum upgrade" trigger from Settings
  useEffect(() => {
    if (externalOpenSanctumUpgrade) {
      // Microtask to ensure state settles before opening
      Promise.resolve().then(() => {
        openUpgradeSanctum();
        onExternalOpenSanctumUpgradeHandled?.();
      });
    }
  }, [externalOpenSanctumUpgrade]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Current Plan — temporarily commented out
      <ActivePlanPanel
        onComparePlansRequested={onComparePlansRequested}
        onCancelPlan={onCancelPlan}
      />
      */}

      {/* Payment Method */}
      <PaymentMethodPanel profileEmail={profileEmail} />

      {/* Invoices */}
      <InvoicesPanel />

      {/* Billing Details */}
      <BillingDetailsPanel />

      {/* Upgrade Plan Modal (trial) */}
      <UpgradePlanModal
        open={upgradeTrialOpen}
        onClose={closeUpgradeTrial}
        onConfirm={handleUpgradeConfirmTrial}
        trialEndsOn={formatTrialEndDate(trialEnd)}
        currentPlan={planName || 'Sanctum'}
      />

      {/* Upgrade Now Sanctum Modal */}
      <UpgradeNowSanctumModal
        open={upgradeSanctumOpen}
        onClose={closeUpgradeSanctum}
        onConfirm={handleUpgradeConfirmSanctum}
        currentPlan="Sanctum"
      />
    </>
  );
};
